import React, {
  FC,
  ImgHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import checkInViewIntersectionObserver from "utils/isInViewPortIntersectionObserver";
import PlaceIcon from "./PlaceIcon";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

export interface NcImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  containerClassName?: string;
  fallbackSrc?: string; // New prop for fallback image
}

const NcImage: FC<NcImageProps> = ({
  containerClassName = "coverthebase",
  alt = "nc-imgs",
  src = "",
  fallbackSrc = "https://argauto.lv/application/modules/themes/views/default/assets/images/image-placeholder.png",
  className = "object-cover w-full h-full",
  ...args
}) => {
  const isMounted = useRef(false);  // ← CHANGE 1: was `let isMounted = false`
  const _containerRef = useRef(null);
  const _imageElRef = useRef<HTMLImageElement | null>(null);

  const [__src, set__src] = useState("");
  const [imageLoaded, setImageLoaded] = useState(false);

  const _initActions = async () => {
    _checkInViewPort();
  };

  const _checkInViewPort = () => {
    if (!_containerRef.current) return;
    checkInViewIntersectionObserver({
      target: _containerRef.current,
      options: {
        root: null,
        rootMargin: "0%",
        threshold: 0,
      },
      freezeOnceVisible: true,
      callback: _imageOnViewPort,
    });
  };

  const _imageOnViewPort = () => {
    if (!src) {
      _handleImageLoaded();
      return true;
    }
    _imageElRef.current = new Image();
    if (_imageElRef.current) {
      _imageElRef.current.src = src;
      _imageElRef.current.addEventListener("load", _handleImageLoaded);
      _imageElRef.current.addEventListener("error", _handleImageError);
    }
    return true;
  };

  const _handleImageLoaded = () => {
    if (!isMounted.current) return;  // ← CHANGE 2: was `if (!isMounted)`
    setImageLoaded(true);
    set__src(src);
  };

  const _handleImageError = () => {
    if (!isMounted.current) return;  // ← CHANGE 3: was `if (!isMounted)`
    set__src(fallbackSrc);
    setImageLoaded(true);
  };

  useEffect(() => {
    isMounted.current = true;
    _initActions();
    return () => {
      isMounted.current = false;
      // Abort any in-flight preload to prevent stale callbacks
      if (_imageElRef.current) {
        _imageElRef.current.src = "";
        _imageElRef.current = null;
      }
    };
  }, [src]);

  const renderLoadingPlaceholder = () => {
    return (
      <div
        className={`${className} flex items-center justify-center bg-neutral-200 dark:bg-neutral-6000 text-neutral-100 dark:text-neutral-500`}
      >
        <div className="h-2/4 max-w-[50%]">
          <PlaceIcon />
        </div>
      </div>
    );
  };

  return (
    <div
      className={`nc-NcImage ${containerClassName}`}
      data-nc-id="NcImage"
      ref={_containerRef}
    >
      {__src && imageLoaded ? (
        <Zoom>
          <img
            src={__src ? __src : fallbackSrc}
            className={className}
            alt={alt}
            onError={_handleImageError}
            {...args}
          />
        </Zoom>
      ) : (
        renderLoadingPlaceholder()
      )}
    </div>
  );
};

export default NcImage;
